<template>
    <div>
        <rkm-area
            no-margin-top
            title="Filtros"
            type="fieldset"
        >
            <rkm-form
                v-if="model"
                ref="RkmForm"
            >
                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-date-picker
                            :end-validations="validations.periodo.end"
                            :max-date="additionals.dataAtual"
                            :range-inline="false"
                            :start-validations="validations.periodo.start"
                            end-label="Até"
                            range
                            start-label="Período de"
                            v-model="model.periodo"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="!model.periodo.start || !model.periodo.end"
                            :options="form.unidades"
                            @change="handleUnidadeChange"
                            default-option-text="Todas"
                            label="Unidade"
                            show-default-option
                            text-field="nomeFantasia"
                            v-model="model.unidadeId"
                            value-field='id'
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="!model.periodo.start || !model.periodo.end"
                            :options="form.infraestruturas"
                            @change="handleInfraestruturaChange"
                            default-option-text="Todos"
                            label="Local de atendimento"
                            show-default-option
                            text-field="nome"
                            v-model="model.infraestruturaId"
                            value-field='id'
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="!model.periodo.start || !model.periodo.end"
                            :options="form.grupos"
                            @change="handleGrupoChange"
                            default-option-text="Todos"
                            label="Grupo"
                            show-default-option
                            text-field="nome"
                            v-model="model.grupoId"
                            value-field='id'
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-input-autocomplete
                            :disabled="!model.periodo.start || !model.periodo.end"
                            :repository="repositories.relatorioPacienteAtendido.material"
                            :request-query-string-params="requestQueryStringParamsMaterial"
                            :search-fields="[{ key: 'cadastro_material.descricao', searchable: true }]"
                            label="Medicamento"
                            text-field="descricao"
                            v-model="model.material"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-button
                            :disabled="!model.periodo.start || !model.periodo.end"
                            :label="false"
                            @click="handleBuscarClick"
                            color="primary"
                        >
                            Buscar
                        </rkm-button>

                        <rkm-button
                            :disabled="!model.periodo.start || !model.periodo.end"
                            :label="false"
                            @click="handleLimparClick"
                        >
                            Limpar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </rkm-area>

        <rkm-area
            v-if="itens.length > 0"
            no-margin-top
            title="Resultado"
        >
            <template v-slot:trailing>
                <rkm-dropdown
                    class="mr-2"
                    color="primary"
                    cols="1"
                    menu-class="auto-scroll"
                    no-caret
                    small
                    title="Colunas"
                    v-rkm-tooltip.hover
                >
                    <template v-slot:button-content>
                        <rkm-icon icon="columns" />
                    </template>

                    <rkm-form-checkbox
                        :indeterminate="indeterminate"
                        @change="handleTodosClick"
                        class="mx-2"
                        v-model="selectedAll"
                    >
                        Todas
                    </rkm-form-checkbox>

                    <rkm-form-checkbox-group
                        :options="optionalColumns"
                        class="mx-2"
                        stacked
                        text-field="label"
                        v-model="selectedColumns"
                        value-field="key"
                    />
                </rkm-dropdown>

                <rkm-button
                    :disabled="itens.length === 0"
                    @click="handleImprimirClick"
                    color="info"
                    small
                    title="Imprimir"
                >
                    <rkm-icon icon="print" />
                </rkm-button>
            </template>

            <rkm-table
                v-for="(item, index) in itens"
                :class="{ 'mb-3': index < itens.length - 1 }"
                :fields="fields"
                :items="item.entregas"
                :key="item.pacienteId"
                class="table-resultado"
                hide-filters
                hide-pagination
            >
                <template v-slot:beforeTable>
                    <div class="table-resultado-header-footer">
                        <rkm-row>
                            <rkm-col>
                                <strong>Paciente:</strong> {{ item.pacienteNome }}
                            </rkm-col>

                            <rkm-col cols="auto">
                                <strong>Prontuário:</strong> {{ item.pacienteProntuario }}
                            </rkm-col>
                        </rkm-row>
                    </div>
                </template>

                <template v-slot:afterTable>
                    <div class="table-resultado-header-footer text-center">
                        <span>
                            <strong>Total de dispensações:</strong> {{ item.dispensacoes }}
                        </span>
                    </div>
                </template>
            </rkm-table>

            <div class="mt-3 footer-totalizadores d-flex justify-content-around">
                <span><strong>Total de dispensações:</strong> {{ getSafely(() => totalizadores.dispensacoes, 0) }}</span>
            </div>
        </rkm-area>
    </div>
</template>

<script>
// Mixins.
import { error, object, repository, stringCase, formats } from '@/mixins'

// Others.
import moment from 'moment'

// Vuex.
import { mapActions } from 'vuex'

export default {
    computed: {
        fields() {
            return this.columns.filter(column => (column.fixed || false) || this.selectedColumns.includes(column.key))
        },
        optionalColumns() {
            return this.columns.filter(column => !(column.fixed || false))
        },
        validations() {
            return {
                periodo: {
                    start: { required: true },
                    end: { required: true },
                }
            }
        },
        requestQueryStringParamsMaterial() {
            const startDate = this.others.moment(this.model.periodo.start).format('YYYY-MM-DD')
            const endDate = this.others.moment(this.model.periodo.end).format('YYYY-MM-DD')

            const request = {
                startDate,
                endDate,
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
                grupoId: this.model.grupoId,
            }

            return this.camelObjectKeysToSnakeRecursive(request)
        },
    },
    created() {
        this.repositories = {
            impressao: this.getConectaSusRepository('impressao.farmacia'),
            relatorioPacienteAtendido: this.getConectaSusRepository('farmacia.relatorioPacienteAtendido'),
        }
    },
    data() {
        return {
            additionals: null,
            columns: [
                {
                    key: 'dataDispensacao',
                    label: 'Data de dispensação',
                    class: 'col-min text-center',
                    fixed: true,
                    sortable: true,
                    formatter: value => this.formatDate(value),
                    filterByFormatted: true,
                },
                { key: 'unidadeNomeFantasia', label: 'Unidade', fixed: true, sortable: true },
                { key: 'infraestruturaNome', label: 'Local de atendimento', sortable: true },
                { key: 'materialDescricao', label: 'Medicamento', fixed: true, sortable: true },
                { key: 'grupoNome', label: 'Grupo', sortable: true },
                { key: 'quantidade', label: 'Quantidade', class: 'col-min text-center', fixed: true, sortable: true },
            ],
            form: null,
            indeterminate: false,
            itens: [],
            model: null,
            others: { moment },
            selectedAll: true,
            selectedColumns: [
                'dataDispensacao',
                'unidadeNomeFantasia',
                'infraestruturaNome',
                'materialDescricao',
                'grupoNome',
                'quantidade',
            ],
        }
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {
            this.setLoading()

            this.repositories.relatorioPacienteAtendido.create()
                .then(response => {
                    this.additionals = Object.assign(response.data.additionals)
                    this.form = Object.assign(response.data.form)
                    this.model = Object.assign(response.data.model)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleUnidadeChange(value) {
            this.form.infraestruturas = []
            this.form.grupos = []
            this.model.infraestruturaId = null
            this.model.grupoId = null

            this.model.material = {
                id: null,
                descricao: null,
            }

            this.itens = []

            if (value) {
                const startDate = this.others.moment(this.model.periodo.start).format('YYYY-MM-DD')
                const endDate = this.others.moment(this.model.periodo.end).format('YYYY-MM-DD')

                this.getInfraestruturas(startDate, endDate, value)
            }
        },
        handleInfraestruturaChange(value) {
            this.form.grupos = []
            this.model.grupoId = null

            this.model.material = {
                id: null,
                descricao: null,
            }

            this.itens = []

            if (value) {
                const startDate = this.others.moment(this.model.periodo.start).format('YYYY-MM-DD')
                const endDate = this.others.moment(this.model.periodo.end).format('YYYY-MM-DD')

                this.getGrupos(startDate, endDate, value)
            }
        },
        handleGrupoChange() {
            this.model.material = {
                id: null,
                descricao: null,
            }

            this.itens = []
        },
        handleBuscarClick() {
            this.setLoading()

            const request = {
                startDate: this.others.moment(this.model.periodo.start).format('YYYY-MM-DD'),
                endDate: this.others.moment(this.model.periodo.end).format('YYYY-MM-DD'),
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
                grupoId: this.model.grupoId,
                materialId: this.model.material ? this.model.material.id : null,
            }

            this.repositories.relatorioPacienteAtendido.index(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.itens = Object.assign(response.data.pacientes)
                    this.totalizadores = response.data.totalizadores
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleLimparClick() {
            this.form.unidades = []
            this.form.infraestruturas = []
            this.form.grupos = []

            this.model.periodo = {
                start: null,
                end: null,
            }

            this.model.unidadeId = null
            this.model.infraestruturaId = null
            this.model.grupoId = null

            this.model.material = {
                id: null,
                descricao: null,
            }

            this.itens = []
        },
        handleTodosClick(selected) {
            this.selectedColumns = selected ? this.optionalColumns.map(c => c.key) : []
        },
        handleImprimirClick() {
            this.setLoading()

            const request = {
                startDate: this.others.moment(this.model.periodo.start).format('YYYY-MM-DD'),
                endDate: this.others.moment(this.model.periodo.end).format('YYYY-MM-DD'),
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
                grupoId: this.model.grupoId,
                materialId: this.model.material ? this.model.material.id : null,
                selectedColumns: this.fields.map(field => ({
                    class: field.class,
                    key: this.camelToSnake(field.key),
                    label: field.label,
                })),
            }

            this.repositories.impressao.relatorioPacienteAtendido(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    const content = new Blob([response.data], {type: response.headers['content-type']})
                    const url = URL.createObjectURL(content)
                    const link = document.createElement('a')

                    link.href = url
                    link.target = '_blank'
                    link.click()
                    link.remove()
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getUnidades(startDate, endDate) {
            this.setLoading()

            const request = {
                listar: 'unidades',
                startDate,
                endDate,
            }

            this.repositories.relatorioPacienteAtendido.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.unidades = Object.assign(response.data)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getInfraestruturas(startDate, endDate, unidadeId) {
            this.setLoading()

            const request = {
                listar: 'infraestruturas',
                startDate,
                endDate,
                unidadeId,
            }

            this.repositories.relatorioPacienteAtendido.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.infraestruturas = Object.assign(response.data)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getGrupos(startDate, endDate, infraestruturaId) {
            this.setLoading()

            const request = {
                listar: 'grupos',
                startDate,
                endDate,
                infraestruturaId,
            }

            this.repositories.relatorioPacienteAtendido.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.grupos = Object.assign(response.data)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
    },
    mounted() {
        this.load()
    },
    mixins: [ error, object, repository, stringCase, formats ],
    watch: {
        'model.periodo': {
            deep: true,
            handler(value) {
                if (value.start && value.end) {
                    const startDate = this.others.moment(value.start).format('YYYY-MM-DD')
                    const endDate = this.others.moment(value.end).format('YYYY-MM-DD')

                    this.getUnidades(startDate, endDate)
                }
            },
        },
        selectedColumns(value) {
            this.indeterminate = value.length > 0 && value.length != this.optionalColumns.length
            this.selectedAll = value.length > 0 && value.length == this.optionalColumns.length
        },
    },
}
</script>

<style scoped>
.dropdown >>> .auto-scroll {
    max-height: 400px;
    overflow-y: auto;
    width: 300px;
}
.table-resultado >>> .table-resultado-header-footer, .footer-totalizadores {
    color: var(--gray-dark);
    font-weight: 600;
    background: #f1f1f1;
    padding: 0.5rem;
    border: 1px solid #dee2e6;
}
</style>